import BezierCurve from 'bezier-js';
import {
  getLength, getOffset, convertSegmentIntoBezierCurves,
  getSegmentBezierCurve, getT, split, convertPointPairToBezierJS,
  getDistanceT, getPointPair, getBounds, getNormalTLength, getTagentTLength,
  getPointDistance, convertKnotPairToBezier, createGradeBazierCurve,
} from './BezierHelper';

import SVGHelper from './SVGHelper';
import { InterpolateSmoothHandles, InterpolateHandles, GetAbsLH, GetAbsRH, Reverse, immutableInterpolateSmoothHandles, immutableInterpolateHandles, GetRelRH, GetRelLH } from './KnotHelper';
import MathHelper from './MathHelper';
import { DART_LEFT, DART_APEX, DART_RIGHT, DART_PIVOT } from '../constants/knotTypes';
import { PIXEL_PER_CM } from '../constants/units';
import PieceHelper from './PieceHelper';

export default class SegmentHelper {
  static getNextDartRightIndex(knots, index) {
    return knots.findIndex((knot, idx) => idx > index && knot.type === DART_RIGHT);
  }

  static getSegmentOffset(knots, allowance) {
    let segmentOffset = [];
    for (let i = 0; i < knots.length - 1; i += 1) {
      if (knots[i].type === DART_LEFT) {
        if (i !== 0) {
          const dartRightIndex = this.getNextDartRightIndex(knots, i);
          if (dartRightIndex !== -1) {
            const p1 = segmentOffset[segmentOffset.length - 1].points[3];
            const dartRightBezier = convertKnotPairToBezier(knots[dartRightIndex], knots[dartRightIndex + 1]);
            const dartRightOffset = getOffset(dartRightBezier, allowance);
            const p2 = dartRightOffset[0].points[0];
            const line = convertPointPairToBezierJS(p1, p2);

            segmentOffset = segmentOffset.concat(line);
          }
        }
      }

      if (knots[i].type !== DART_LEFT && knots[i].type !== DART_APEX) {
        const bezier = convertKnotPairToBezier(knots[i], knots[i + 1]);
        segmentOffset = segmentOffset.concat(getOffset(bezier, allowance));
      }
    }

    return segmentOffset;
  }

  static getSegmentOffsetPath(knots, allowance) {
    const segmentOffset = this.getSegmentOffset(knots, allowance);
    return segmentOffset.map((so) => SVGHelper.createSVGPathFromBezierJS([so]));
  }

  // TODO, rename this so it doesnt conflict with BezierHelper.getLenght();
  static getLength(knots) {
    const beziers = convertSegmentIntoBezierCurves(knots);
    return this.getLengthBeziers(beziers);
  }

  /**
   * The length of a line, not counting the legs of any darts on it.
   * A line with a dart runs from one real corner to the next and passes through the dart's left, apex
   * and right points. The dart's two legs (left to apex, apex to right) are not part of the line: the
   * line is what is left either side of the dart. A line is the same length before and after a dart is
   * added to it, so this is the length to show and to change.
   * @param {Array<Object>} knots the line's knots, with their curve handles filled in
   */
  static getLengthWithoutDarts(knots) {
    const beziers = convertSegmentIntoBezierCurves(knots);
    let length = 0;
    beziers.forEach((bezier, idx) => {
      const from = knots[idx].type;
      const to = knots[idx + 1].type;
      const isDartLeg = (from === DART_LEFT && to === DART_APEX) || (from === DART_APEX && to === DART_RIGHT);
      if (!isDartLeg) length += getLength(bezier);
    });
    return length;
  }

  /**
   *
   * @param {Array<Array>} gradesSegment
   * @return {Array<object>} this will return the coordenates, distance, and color of each notch.
   */
  static getGradesNotchesPointPair({ gradesSegment, distance, segmentLength }) {
    // get the proportion of the base segment
    const proportion = (100 * distance) / segmentLength;

    return gradesSegment.map((grade) => {
      let pointPair;
      const gradeLength = grade.length - 1;
      const gradeBezierCurve = createGradeBazierCurve(grade);
      let lengthCurve = getLength(gradeBezierCurve[0]);
      const firstSegment = gradeBezierCurve[0];

      // if the segment has more than two knots (example: sleeve with three knots) this will support it
      if (gradeLength > 1) {
        const secondSegment = gradeBezierCurve[1];
        lengthCurve += getLength(secondSegment);
        const gradeDistance = (proportion * lengthCurve) / 100;
        if (getLength(firstSegment) < gradeDistance) {
          // the easy way that I found was subtract the length of the first segment and the remainder
          // will be the distance in the second segment
          pointPair = getPointPair(secondSegment, gradeDistance - getLength(firstSegment));
        } else {
          pointPair = getPointPair(firstSegment, gradeDistance);
        }
      } else {
        // if the segment has only two knots
        const gradeDistance = (proportion * lengthCurve) / 100;
        pointPair = getPointPair(firstSegment, gradeDistance);
      }

      return {
        pointPair,
        distance,
        color: grade[0].color,
      };
    });
  }

  /**
   * Get a new set of knots at the offset from the centroid.
   */
  static getGradedKnots({ centroid, knots, index, offset }) {
    const gradeKnots = [];

    // Frist we clone the knots
    for (let i = 0; i < knots.length; i++) {
      gradeKnots[i] = { ...knots[i] };
    }

    const last = knots.length - 1;
    for (let i = 0; i <= last; i++) {
      // if we are with a segment of three
      const ni = i == last ? i - 1 : i + 1;
      const knot = knots[i];
      const next = knots[ni];
      const distance = MathHelper.getDistance(knot, next);
      const rH = GetRelRH(knot);
      const lH = GetRelLH(next);

      // Initializse the arrays on these
      if (typeof knot.grades === 'undefined') {
        knot.grades = [];
      }
      if (typeof next.grades === 'undefined') {
        next.grades = [];
      }

      // If this knot did not come with grades, we interpolate
      let knotGrade = knot.grades[index];
      if (!knotGrade) {
        knotGrade = PieceHelper.getKnotOffsetAsDelta(centroid, knot, offset);
      }

      // If the next knot did not come with grades, we interpolate
      let nextGrade = next.grades[index];
      if (!nextGrade) {
        nextGrade = PieceHelper.getKnotOffsetAsDelta(centroid, next, offset);
      }

      // Add the offset to the knot to get the absolute positions
      const knotG = MathHelper.addPoints(knot, knotGrade);
      const nextG = MathHelper.addPoints(next, nextGrade);

      // Get the distance betweeen the graded knot absolut positions
      const distanceG = MathHelper.getDistance(knotG, nextG);


      let ratio = distanceG / distance;
      // eslint-disable-next-line no-restricted-globals
      if (ratio === Infinity || isNaN(ratio)) {
        ratio = 1;
      }
      const lHGrade = typeof gradeKnots[i] !== 'undefined' ? gradeKnots[i].lH : 0;

      // add the graded knot to the grade lines array
      gradeKnots[i].x = knotG.x;
      gradeKnots[i].y = knotG.y;
      if (rH) {
        gradeKnots[i].rH = MathHelper.scalePoint(rH, ratio);
      }
      if (lH) {
        gradeKnots[ni].lH = MathHelper.scalePoint(lH, ratio);
      }
    }
    return gradeKnots;
  }

  static getLengthBeziers(beziers) {
    let length = 0;
    beziers.forEach((bezier) => {
      length += getLength(bezier);
    });
    return length;
  }

  static setLength(knots, length, recursiveCounter = 0) {
    let k = knots.slice();
    const startKnot = k[0];
    const endKnot = k[k.length - 1];

    // Darts on the line do not count towards its length, and are not moved here.
    const delta = length - this.getLengthWithoutDarts(k);

    const startPosition = MathHelper.movePoint(startKnot, GetAbsRH(startKnot), -delta / 2);
    startKnot.x -= startPosition.x;
    startKnot.y -= startPosition.y;
    const endPosition = MathHelper.movePoint(endKnot, GetAbsLH(endKnot), -delta / 2);
    endKnot.x -= endPosition.x;
    endKnot.y -= endPosition.y;

    if (this.getLengthWithoutDarts(k) < length && MathHelper.notEquals(this.getLengthWithoutDarts(k), length) && recursiveCounter < 5) {
      const resultKnots = this.setLength(k, length, recursiveCounter + 1);
      if (this.getLengthWithoutDarts(resultKnots) < length) {
        k = resultKnots;
      }
    }

    return k;
  }

  static getSegmentProjection(knots, point) {
    const beziers = convertSegmentIntoBezierCurves(knots);
    let bezierPoint = null;
    let distance = Number.MAX_VALUE;

    beziers.forEach((bezier, idx) => {
      const projection = bezier.project(point);

      if (MathHelper.getDistance(point, projection) < distance) {
        distance = MathHelper.getDistance(point, projection);
        bezierPoint = { bezierIdx: idx, ...projection };
      }
    });

    return bezierPoint;
  }

  static getBezierPoint(knots, bezierIdx, t) {
    const bezier = getSegmentBezierCurve(knots, bezierIdx);

    return getT(bezier, t);
  }

  static splitBezier(knots, bezierIdx, t) {
    const bezier = getSegmentBezierCurve(knots, bezierIdx);

    return split(bezier, t);
  }

  static getNormalTLength(knots, bezierIdx, t, length) {
    const bezier = getSegmentBezierCurve(knots, bezierIdx);

    return getNormalTLength(bezier, t, length);
  }

  static getTagentTLength(knots, bezierIdx, t, length) {
    const bezier = getSegmentBezierCurve(knots, bezierIdx);

    return getTagentTLength(bezier, t, length);
  }

  static getPointDistance(knots, bezierIdx, t) {
    const beziers = convertSegmentIntoBezierCurves(knots);
    let distance = 0;

    beziers.forEach((bezier, idx) => {
      if (idx < bezierIdx) {
        distance += getLength(bezier);
      } else if (idx === bezierIdx) {
        distance += getDistanceT(bezier, t);
      }
    });

    return distance;
  }


  static getNotchPointPair(knots, distance, seam = null, length = PIXEL_PER_CM) {
    const beziers = convertSegmentIntoBezierCurves(knots);
    let bezierDistance = distance;

    for (let i = 0; i < beziers.length; i += 1) {
      const bezier = beziers[i];
      const bezierLength = getLength(bezier);

      if (bezierLength < bezierDistance) {
        if (i === beziers.length - 1) {
          // Get point pair at the end of the bezier
          return getPointPair(bezier, bezierLength, seam, length);
        }

        bezierDistance -= bezierLength;
      } else {
        return getPointPair(bezier, bezierDistance, seam, length);
      }
    }

    return null;
  }

  static getSegment(knotSelected, piece) {
    let segment = [];

    const knotsPiece = piece.knots;
    const knotsSegment = knotsPiece.filter((knotPiece) => knotPiece.id === knotSelected[0] || knotPiece.id === knotSelected[1]);

    segment = knotsSegment;
    return segment;
  }

  static getBounds(knots, seam) {
    const beziers = convertSegmentIntoBezierCurves(knots);
    const segmentBounds = beziers.reduce((bounds, bezier) => {
      const bezierBounds = getBounds(bezier);
      const { x, y, w, h } = bezierBounds;

      const xMin = x < bounds.xMin ? x : bounds.xMin;
      const xMax = x + w > bounds.xMax ? x + w : bounds.xMax;
      const yMin = y < bounds.yMin ? y : bounds.yMin;
      const yMax = y + h > bounds.yMax ? y + h : bounds.yMax;

      return { xMin, xMax, yMin, yMax };
    }, { xMin: Number.MAX_VALUE, xMax: 0, yMin: Number.MAX_VALUE, yMax: 0 });

    if (seam) {
      segmentBounds.xMin -= seam.allowance;
      segmentBounds.xMax += seam.allowance;
      segmentBounds.yMin -= seam.allowance;
      segmentBounds.yMax += seam.allowance;
    }

    return { x: segmentBounds.xMin, y: segmentBounds.yMin, w: (segmentBounds.xMax - segmentBounds.xMin), h: (segmentBounds.yMax - segmentBounds.yMin) };
  }

  static getSegmentPointAtDistance(knots, distance) {
    const beziers = convertSegmentIntoBezierCurves(knots);
    let bezierDistance = distance;

    for (let i = 0; i < beziers.length; i += 1) {
      const bezier = beziers[i];
      const bezierLength = getLength(bezier);

      if (bezierLength < bezierDistance) {
        if (i === beziers.length - 1) {
          return {
            bezierIdx: i,
            ...getT(bezier, 1),
          };
        }

        bezierDistance -= bezierLength;
      } else {
        return {
          bezierIdx: i,
          ...getPointDistance(bezier, bezierDistance),
        };
      }
    }

    return null;
  }

  static getSegmentPointAndAngleAtDistance(knots, distance) {
    const { bezier, bezierDistance } = this.getSegmentBezierAtDistance(knots, distance);

    const point = getPointDistance(bezier, bezierDistance);
    const nextPointDistance = getLength(bezier) / 20;
    const nextPoint = getPointDistance(bezier, bezierDistance + nextPointDistance);
    const pointAngle = MathHelper.round(MathHelper.getAnglePoints(point, nextPoint), 3);

    return { point, pointAngle };
  }

  static reverse(segment) {
    return segment.map((knot) => Reverse(knot)).reverse();
  }

  static getSegmentBezierAtDistance(knots, distance) {
    const beziers = convertSegmentIntoBezierCurves(knots);

    const getBezier = (arrBeziers, currentDistance) => {
      if (!arrBeziers.length) throw new Error('not found bezier at distance.');

      const [currentBezier] = arrBeziers;
      const lengthCurrBez = getLength(currentBezier);

      return lengthCurrBez < currentDistance
        ? getBezier(arrBeziers.slice(1), currentDistance - lengthCurrBez)
        : { bezier: currentBezier, bezierDistance: currentDistance };
    };

    return getBezier(beziers, distance);
  }

  static interpolateKnots(knots, isClosed = false) {
    const newKnots = knots.slice();

    const prev = () => isClosed ? newKnots[newKnots.length - 1] : null;
    const next = () => isClosed ? newKnots[0] : null;

    newKnots[0] = InterpolateSmoothHandles(newKnots[0], prev(), newKnots[1]);

    for (let i = 1; i < newKnots.length - 1; i += 1) {
      newKnots[i] = InterpolateSmoothHandles(newKnots[i], newKnots[i - 1], newKnots[i + 1]);
    }

    newKnots[newKnots.length - 1] = InterpolateSmoothHandles(newKnots[newKnots.length - 1],
      newKnots[newKnots.length - 2], next());

    newKnots[0] = InterpolateHandles(newKnots[0], prev(), newKnots[1]);

    for (let j = 1; j < newKnots.length - 1; j += 1) {
      newKnots[j] = InterpolateHandles(newKnots[j], newKnots[j - 1], newKnots[j + 1]);
    }

    newKnots[newKnots.length - 1] = InterpolateHandles(newKnots[newKnots.length - 1], newKnots[newKnots.length - 2], next());

    return newKnots;
  }

  static immutableInterpolateKnots(knots, isClosed = false) {
    const [prev, next] = isClosed
      ? [() => knots.get(knots.size - 1), () => knots.get(0)]
      : [() => null, () => null];

    const interpolate = (oldKnots, interpolateMethod) => {
      let newKnots = oldKnots.set(0, interpolateMethod(knots.get(0), prev(), knots.get(1)));

      for (let key = 1; key < newKnots.size - 1; key += 1) {
        newKnots = newKnots.set(key, interpolateMethod(newKnots.get(key), newKnots.get(key - 1), newKnots.get(key + 1)));
      }

      return newKnots.set(newKnots.size - 1, interpolateMethod(newKnots.get(newKnots.size - 1), newKnots.get(newKnots.size - 2), next()));
    };

    return interpolate(interpolate(knots, immutableInterpolateSmoothHandles), immutableInterpolateHandles);
  }

  // TODO: Evaluate the possibility of create DartHelper file and move these functions to that file

  static isDartSegment(piece, knotsId) {
    if (knotsId.length < 3) return false;

    const knots = piece.knots.filter((knot) => knotsId.indexOf(knot.id) >= 0);

    for (let i = 0; i < knots.length; i += 1) {
      if (!knots[i].type || !knots[i].type.includes('DART')) return false;
    }

    let matches = 0;
    piece.darts.forEach((darts) => {
      const { knots: dartKnots } = darts;
      const dartIds = Object.values(dartKnots);
      knotsId.forEach((knotId) => {
        if (dartIds.indexOf(knotId) >= 0) {
          matches += 1;
        }
      });
    });

    if (matches !== knotsId.length) {
      return false;
    }

    return true;
  }

  static getDartKnots(piece, knots) {
    const dartKnots = piece.knots.filter((k) => k.id === knots[0] || k.id === knots[1] || k.id === knots[2] || k.id === knots[3]);
    if (dartKnots[0].type === DART_PIVOT) {
      dartKnots.push(dartKnots.shift()); // sort the array to have the dart left as first element
    }

    return {
      dartLeft: dartKnots[0],
      dartApex: dartKnots[1],
      dartRight: dartKnots[2],
      dartPivot: dartKnots[3],
    };
  }

  static getDartData(piece, knots) {
    const { darts } = piece;
    const index = darts.findIndex((dart) => dart.knots.dartLeftId === knots[0].id);
    return piece.darts[index];
  }

  static areSegmentsEquals(seg1, seg2) {
    if (seg1.length !== seg2.length) return false;

    for (let i = 0; i < seg1.length; i += 1) {
      if (seg1[i].id !== seg2[i].id) return false;
    }
    return true;
  }

  static removeDart(piece, knot, isDartPivot = false) {
    const { darts, knots } = piece;
    const newPiece = piece;
    let index = -1;
    const dartsAux = [];
    darts.forEach((dart, i) => {
      const knotsArray = Object.values(dart.knots);
      if (knotsArray.findIndex((k) => k === knot.id) >= 0) {
        index = i;
      } else {
        dartsAux.push(dart);
      }
    });
    // remove data darts from array
    const dartsToRemove = Object.values(darts[index].knots);
    newPiece.darts = dartsAux;
    const knotsAux = [];

    // remove dart type property to the knots
    knots.forEach((k) => {
      if (dartsToRemove.includes(k.id)) {
        const newKnot = k;
        if (isDartPivot && k.type && k.type === 'DART_PIVOT') {
          newKnot.corner = true;
        }

        delete newKnot.type;
        knotsAux.push(newKnot);
      } else {
        knotsAux.push(k);
      }
    });

    newPiece.knots = knotsAux;
    return newPiece;
  }
}
